import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, Download, BookOpen, User, Calendar, X } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import PdfViewer from "@/components/PdfViewer";

export default function ArchivesAcademiques() {
  const [q, setQ] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [year, setYear] = useState("");
  const [author, setAuthor] = useState("");
  const [director, setDirector] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [facets, setFacets] = useState({ disciplines: [], years: [] });
  const [disciplines, setDisciplines] = useState([]);
  const [years, setYears] = useState([]);
  const [viewer, setViewer] = useState(null);

  const search = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (discipline) params.discipline = discipline;
      if (year) params.year = year;
      if (author) params.author = author;
      if (director) params.director = director;
      const { data } = await api.get("/archives/search", { params });
      setResults(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/lists/disciplines").then((r) => setDisciplines(r.data));
    api.get("/academic-years").then((r) => setYears(r.data));
    api.get("/archives/facets/summary").then((r) => setFacets(r.data));
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => { e.preventDefault(); search(); };
  const reset = () => { setQ(""); setDiscipline(""); setYear(""); setAuthor(""); setDirector(""); setTimeout(search, 0); };

  const hero = useMemo(() => "/media/research-lab.jpg", []);

  return (
    <div data-testid="archives-page">
      <section className="relative h-[38vh] min-h-[300px] overflow-hidden">
        <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-veil" />
        <div className="relative h-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-12">
          <div className="eyebrow" style={{ color: "#E5C158" }}>Dépôt institutionnel</div>
          <h1 className="mt-3 text-white text-4xl sm:text-5xl font-bold tracking-tight">
            Dépôt institutionnel
          </h1>
          <p className="mt-3 text-white/85 max-w-3xl">
            Le dépôt institutionnel officiel de l'UNISIC. Toutes les productions scientifiques
            validées de l'université — mémoires, projets tutorés, thèses, articles.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-unisic-gold" />
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <form onSubmit={submit} className="paper-card p-6" data-testid="archives-search-form">
          <div className="grid gap-4 md:grid-cols-6">
            <div className="md:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Rechercher</label>
              <div className="mt-1 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Titre, résumé, mots-clés..."
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-sm text-sm"
                  data-testid="input-search"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Discipline</label>
              <select value={discipline} onChange={(e) => setDiscipline(e.target.value)} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm bg-white" data-testid="filter-discipline">
                <option value="">Toutes</option>
                {disciplines.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Année</label>
              <select value={year} onChange={(e) => setYear(e.target.value)} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm bg-white" data-testid="filter-year">
                <option value="">Toutes</option>
                {years.map((y) => <option key={y.id} value={y.label}>{y.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Auteur</label>
              <input value={author} onChange={(e) => setAuthor(e.target.value)} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="filter-author" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-600">Directeur</label>
              <input value={director} onChange={(e) => setDirector(e.target.value)} className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm text-sm" data-testid="filter-director" />
            </div>
          </div>
          <div className="mt-5 flex justify-between items-center">
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <Filter className="w-3 h-3" /> {results.length} résultat(s)
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={reset} className="text-sm text-slate-600 hover:text-unisic-blue" data-testid="btn-reset">
                Réinitialiser
              </button>
              <button type="submit" className="btn-primary text-sm" data-testid="btn-search">
                <Search className="w-4 h-4" /> Rechercher
              </button>
            </div>
          </div>
        </form>

        {/* Facets suggestion */}
        {facets.disciplines?.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-2 items-center">
            <span className="text-xs uppercase tracking-widest text-slate-500">Disciplines populaires :</span>
            {facets.disciplines.slice(0, 6).map((d) => (
              <button
                key={d._id || "other"}
                onClick={() => { setDiscipline(d._id || ""); setTimeout(search, 0); }}
                className="text-xs bg-slate-100 hover:bg-unisic-gold/20 border border-slate-200 px-3 py-1 rounded-full"
                data-testid={`facet-${d._id}`}
              >
                {d._id || "—"} <span className="text-slate-500">({d.count})</span>
              </button>
            ))}
          </div>
        )}

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {loading && (
            <div className="col-span-full text-center py-12 text-slate-500 text-sm">Chargement...</div>
          )}
          {!loading && results.length === 0 && (
            <div className="col-span-full text-center py-16 paper-card p-10">
              <BookOpen className="w-8 h-8 text-slate-300 mx-auto" />
              <p className="mt-4 text-slate-500">Aucun document dans les archives pour l'instant.</p>
              <p className="mt-1 text-xs text-slate-400">Les mémoires validés y sont publiés automatiquement.</p>
            </div>
          )}
          {results.map((doc) => (
            <div key={doc.deposit_id || doc.id} className="paper-card card-hover p-6 flex flex-col" data-testid={`archive-item-${doc.deposit_id}`}>
              <div className="text-[11px] uppercase tracking-widest text-unisic-gold font-semibold">
                {doc.kind || "Mémoire"} · {doc.discipline}
              </div>
              <h3 className="mt-2 font-display font-semibold text-unisic-blue leading-snug">
                {doc.title}
              </h3>
              <p className="mt-3 text-sm text-slate-600 line-clamp-3 flex-1">{doc.abstract}</p>
              <div className="mt-4 pt-4 border-t border-slate-100 space-y-1.5 text-xs text-slate-600">
                <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 text-slate-400" /> {doc.author_name || "—"}</div>
                {doc.director_name && <div className="flex items-center gap-2"><span className="w-3.5 h-3.5" />Directeur : {doc.director_name}</div>}
                <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5 text-slate-400" /> {doc.academic_year || "—"}</div>
              </div>
              {doc.file_path && (
                <div className="mt-5 flex gap-2">
                  <button
                    onClick={() => setViewer({ title: doc.title, url: fileUrl(doc.file_path), filename: `${doc.tracking_number || "document"}.pdf` })}
                    className="btn-primary text-sm flex-1 justify-center"
                    data-testid={`btn-read-${doc.deposit_id}`}
                  >
                    <BookOpen className="w-4 h-4" /> Lire en ligne
                  </button>
                  <a
                    href={fileUrl(doc.file_path)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-secondary text-sm"
                    data-testid={`btn-download-${doc.deposit_id}`}
                  >
                    <Download className="w-4 h-4" />
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center px-2 sm:px-6 py-6 overflow-auto" onClick={() => setViewer(null)} data-testid="archive-pdf-modal">
          <div className="bg-white max-w-4xl w-full rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-display font-semibold text-unisic-blue truncate">{viewer.title}</h3>
              <button onClick={() => setViewer(null)} className="p-2 hover:bg-slate-100 rounded" data-testid="close-archive-modal"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4">
              <PdfViewer url={viewer.url} filename={viewer.filename} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
